import React, { useMemo } from "react";
import { useParams, useLocation, Link } from "react-router-dom";
import blogPosts from "../data/barbequeen-blog-posts.json";
import SEO from "../components/SEO";
import {
  krishnagiriPageSchema,
  coimbatorePageSchema,
  erodePageSchema,
  salemPageSchema,
  pudukkottaiPageSchema,
  bestBbqRestaurantInPudukkottaiSchema,
} from "../data/schemas";

/* ─── Helpers ─────────────────────────────────────────────────── */
const getCategory = (post) => {
  const s = (post.slug || "").toLowerCase();
  const t = (post.title || "").toLowerCase();
  if (s.includes("briyani") || s.includes("biryani") || t.includes("biryani"))
    return "Biryani";
  if (s.includes("grill") || s.includes("bbq") || t.includes("grill"))
    return "BBQ & Grill";
  if (s.includes("catering") || s.includes("event") || t.includes("catering"))
    return "Events";
  if (
    s.includes("restaurant") ||
    s.includes("outlet") ||
    t.includes("restaurant")
  )
    return "Dining";
  if (s.includes("chicken") || t.includes("chicken")) return "Chicken";
  return "Food";
};

const getFeaturedImage = (post) =>
  post.featuredImage ||
  "https://images.unsplash.com/photo-1504674900247-0877df9cc836?q=80&w=1200&auto=format&fit=crop";

const getReadTime = (post) =>
  Math.max(
    2,
    Math.ceil(
      (post.contentBlocks || []).reduce(
        (a, b) => a + (b.text || "").split(/\s+/).length,
        0,
      ) / 200,
    ),
  );

/* ─── Content block cleaner ───────────────────────────────────── */
const NAV_SKIP = new Set([
  "home",
  "about us",
  "menu",
  "franchise",
  "services",
  "outdoor catering",
  "barbequeen express",
  "events",
  "outlets",
  "gallery",
  "outlet gallery",
  "fun activities",
  "contact us",
  "order now",
  "blog",
  "visit us",
]);

const renderFormattedText = (text) => {
  if (!text || typeof text !== "string") return text;
  // Match either [text](url) or **bold**
  const tokenRegex = /(\[([^\]]+)\]\(([^)]+)\)|\*\*([^*]+)\*\*)/g;
  if (!tokenRegex.test(text)) return text;

  tokenRegex.lastIndex = 0;
  const elements = [];
  let lastIdx = 0;
  let match;
  let key = 0;

  while ((match = tokenRegex.exec(text)) !== null) {
    if (match.index > lastIdx) {
      elements.push(text.substring(lastIdx, match.index));
    }
    if (match[2] !== undefined && match[3] !== undefined) {
      const label = match[2];
      const rawUrl = match[3];
      const isInternal =
        rawUrl.startsWith("/") ||
        rawUrl.includes("barbequeen.com") ||
        rawUrl.includes("barbequeen.in");

      if (isInternal) {
        const path =
          rawUrl.replace(/^https?:\/\/(www\.)?barbequeen\.(com|in)/, "") || "/";
        elements.push(
          <Link
            key={`link-${key++}`}
            to={path}
            className="text-[#9b1c2e] font-semibold underline underline-offset-2 hover:text-[#7d1624] transition-colors"
          >
            {label}
          </Link>
        );
      } else {
        elements.push(
          <a
            key={`ext-${key++}`}
            href={rawUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#9b1c2e] font-semibold underline underline-offset-2 hover:text-[#7d1624] transition-colors"
          >
            {label}
          </a>
        );
      }
    } else if (match[4] !== undefined) {
      elements.push(
        <strong key={`bold-${key++}`} className="font-semibold text-gray-900">
          {match[4]}
        </strong>
      );
    }
    lastIdx = tokenRegex.lastIndex;
  }
  if (lastIdx < text.length) {
    elements.push(text.substring(lastIdx));
  }
  return elements;
};

const cleanBlocks = (blocks = []) => {
  if (!blocks || !blocks.length) return [];
  const hasNavHeader = blocks
    .slice(0, 5)
    .some((b) => NAV_SKIP.has((b.text || "").trim().toLowerCase()));
  if (!hasNavHeader) {
    return blocks.filter((b) => (b.text || "").trim().length > 0);
  }

  let started = false;
  const out = [];
  for (const b of blocks) {
    const txt = (b.text || "").trim();
    const low = txt.toLowerCase();
    if (!txt) continue;
    if (!started) {
      if (
        b.type === "heading" &&
        !NAV_SKIP.has(low) &&
        !low.includes("nonvee") &&
        !low.includes("valmiki") &&
        !low.includes("feedback@") &&
        !low.includes("open:")
      ) {
        started = true;
      }
    }
    if (started) {
      const skip =
        NAV_SKIP.has(low) ||
        low.includes("nonvee foods") ||
        low.includes("valmiki street") ||
        low.includes("feedback@barbequeen") ||
        low.includes("open: ") ||
        (low.includes("salem") && low.length < 10);
      if (!skip) out.push(b);
    }
  }
  return out;
};

/* ─── Popular Post sidebar card ────────────────────────────────── */
const PopularCard = ({ post }) => {
  const img = getFeaturedImage(post);
  const cat = getCategory(post);
  return (
    <Link to={`/blog/${post.slug}`} className="group block mb-6 last:mb-0">
      <div className="rounded-xl overflow-hidden mb-3 h-36 bg-gray-100">
        <img
          src={img}
          alt={post.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-400"
          onError={(e) => {
            e.target.src =
              "https://images.unsplash.com/photo-1504674900247-0877df9cc836?q=80&w=600&auto=format&fit=crop";
          }}
        />
      </div>
      <p className="text-[11px] text-gray-400 mb-1">
        <span className="font-semibold text-[#9b1c2e] uppercase tracking-wider">
          {cat}
        </span>
      </p>
      <h4 className="text-[14px] font-semibold text-gray-800 leading-snug line-clamp-2 group-hover:text-[#9b1c2e] transition-colors mb-2">
        {post.title}
      </h4>
      <p className="text-xs text-gray-500 line-clamp-2 leading-relaxed mb-2">
        {post.excerpt}
      </p>
      <span className="inline-flex items-center gap-1 text-[12px] font-semibold text-[#9b1c2e] group-hover:gap-2 transition-all duration-200">
        Learn More
        <svg
          className="w-3.5 h-3.5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2.5}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M17 8l4 4m0 0l-4 4m4-4H3"
          />
        </svg>
      </span>
    </Link>
  );
};

const LANDING_PAGE_SEO = {
  "best-restaurant-in-krishnagiri": {
    title: "Best Restaurant in Krishnagiri | Barbequeen Biryani Grill",
    description:
      "Best biryani, chicken biryani & mandi in Krishnagiri. Charcoal-grilled favourites on the Chennai–Bengaluru highway since 1999. Order online or dine in today.",
    keywords:
      "Best Restaurant in Krishnagiri, Best Biryani in Krishnagiri, Best Chicken Biryani in Krishnagiri, Best Mandi Biryani in Krishnagiri",
    robots: "index, follow",
    canonical: "https://barbequeen.com/best-restaurant-in-krishnagiri",
    geo: {
      region: "IN-TN",
      placename: "Krishnagiri, Tamil Nadu",
      position: "12.5266;78.2141",
    },
    og: {
      type: "website",
      url: "https://barbequeen.com/best-restaurant-in-krishnagiri",
      title: "Best Restaurant in Krishnagiri | Barbequeen Biryani Grill",
      description:
        "Famous biryani, chicken biryani & Arabian mandi in Krishnagiri. A perfect highway stop for dine-in, takeaway & delivery.",
      image: "https://barbequeen.com/images/barbequeen-krishnagiri-og.jpg",
      imageWidth: "1200",
      imageHeight: "630",
      siteName: "Barbequeen Restaurant",
      locale: "en_IN",
    },
    schema: krishnagiriPageSchema,
  },
  "best-restaurant-in-coimbatore": {
    title: "Best Restaurant in Coimbatore | Barbequeen – Grill & Barbecue",
    description:
      "Best restaurant in Coimbatore since 1999. Charcoal grill chicken, biryani & mandi in Saibaba Colony. Order online or dine in.",
    keywords:
      "Best Restaurant in Coimbatore, Best Barbecue in Coimbatore, Best Biryani in Coimbatore",
    robots: "index, follow",
    canonical: "https://barbequeen.com/best-restaurant-in-coimbatore",
    geo: {
      region: "IN-TN",
      placename: "Coimbatore, Tamil Nadu",
      position: "11.0233;76.9505",
    },
    og: {
      type: "website",
      url: "https://barbequeen.com/best-restaurant-in-coimbatore",
      title: "Best Restaurant in Coimbatore | Barbequeen – Grill & Barbecue",
      description:
        "Home of the Double Marinated Grill Chicken. Charcoal barbecue, biryani & Arabian mandi in Coimbatore. Dine-in, takeaway & delivery.",
      image: "https://barbequeen.com/images/barbequeen-coimbatore-og.jpg",
      imageWidth: "1200",
      imageHeight: "630",
      siteName: "Barbequeen Restaurant",
      locale: "en_IN",
    },
    schema: coimbatorePageSchema,
  },
  "best-restaurant-in-erode": {
    title: "Best Restaurant in Erode | Barbequeen – Grill & Barbecue",
    description:
      "Barbequeen — top restaurant in Erode since 1999. Double-marinated charcoal grill chicken, biryani & Arabian mandi on Perundurai Rd. Dine-in or order online.",
    keywords:
      "Best Restaurant in Erode, Best Barbecue in Erode, Best Biryani in Erode",
    robots: "index, follow",
    canonical: "https://barbequeen.com/best-restaurant-in-erode",
    geo: {
      region: "IN-TN",
      placename: "Erode, Tamil Nadu",
      position: "11.3410;77.7172",
    },
    og: {
      type: "website",
      url: "https://barbequeen.com/best-restaurant-in-erode",
      title: "Best Restaurant in Erode | Barbequeen – Grill & Barbecue",
      description:
        "Home of the Double Marinated Grill Chicken. Charcoal barbecue, biryani & Arabian mandi in Erode. Dine-in, takeaway & delivery.",
      image: "https://barbequeen.com/images/barbequeen-erode-og.jpg",
      imageWidth: "1200",
      imageHeight: "630",
      siteName: "Barbequeen Restaurant",
      locale: "en_IN",
    },
    schema: erodePageSchema,
  },
  "best-restaurant-in-salem": {
    title: "Best Restaurant in Salem | Barbequeen – Grill & Barbecue",
    description:
      "Barbequeen — Salem's best restaurant since 1999. Charcoal grill chicken, biryani & Arabian mandi across 8 outlets. Order online or dine in.",
    keywords:
      "Best Restaurant in Salem, Best Barbecue in Salem, Best Biryani in Salem",
    robots: "index, follow",
    canonical: "https://barbequeen.com/best-restaurant-in-salem",
    geo: {
      region: "IN-TN",
      placename: "Salem, Tamil Nadu",
      position: "11.6643;78.1460",
    },
    og: {
      type: "website",
      url: "https://barbequeen.com/best-restaurant-in-salem",
      title: "Best Restaurant in Salem | Barbequeen – Grill & Barbecue",
      description:
        "Home of the Double Marinated Grill Chicken. Charcoal barbecue, biryani & Arabian mandi across 8 Salem outlets. Dine-in, takeaway & delivery.",
      image: "https://barbequeen.com/images/barbequeen-og.jpg",
      imageWidth: "1200",
      imageHeight: "630",
      siteName: "Barbequeen Restaurant",
      locale: "en_IN",
    },
    schema: salemPageSchema,
  },
  "best-restaurant-in-pudukkottai": {
    title: "Best Restaurant in Pudukkottai: Why Families Choose Barbequeen",
    description:
      "Looking for the best restaurant in Pudukkottai? Enjoy authentic wood-fire seeraga samba biryani, double-marinated grills, and family-friendly dining at Barbequeen.",
    keywords:
      "Best restaurant in Pudukkottai, best family restaurant in Pudukkottai, biryani restaurant in Pudukkottai, Barbequeen Pudukkottai, wood fire biryani, seeraga samba biryani, grill chicken Pudukkottai",
    robots: "index, follow",
    canonical: "https://barbequeen.com/blog/best-restaurant-in-pudukkottai",
    geo: {
      region: "IN-TN",
      placename: "Pudukkottai, Tamil Nadu",
      position: "10.3833;78.8001",
      icbm: "10.3833, 78.8001",
    },
    og: {
      type: "article",
      url: "https://barbequeen.com/blog/best-restaurant-in-pudukkottai",
      title: "Best Restaurant in Pudukkottai: Why Families Choose Barbequeen",
      description:
        "Discover wood-fire dum biryani, double-marinated grill chicken, and family-friendly dining at Barbequeen Pudukkottai. Served since 1999.",
      image: "https://barbequeen.com/images/barbequeen-pudukkottai-og.jpg",
      imageWidth: 1200,
      imageHeight: 630,
      siteName: "Barbequeen Restaurant",
      locale: "en_IN",
    },
    schema: pudukkottaiPageSchema,
  },
  "best-restaurant-in-Pudukkottai": {
    title: "Best Restaurant in Pudukkottai: Why Families Choose Barbequeen",
    description:
      "Looking for the best restaurant in Pudukkottai? Enjoy authentic wood-fire seeraga samba biryani, double-marinated grills, and family-friendly dining at Barbequeen.",
    keywords:
      "Best restaurant in Pudukkottai, best family restaurant in Pudukkottai, biryani restaurant in Pudukkottai, Barbequeen Pudukkottai, wood fire biryani, seeraga samba biryani, grill chicken Pudukkottai",
    robots: "index, follow",
    canonical: "https://barbequeen.com/blog/best-restaurant-in-pudukkottai",
    geo: {
      region: "IN-TN",
      placename: "Pudukkottai, Tamil Nadu",
      position: "10.3833;78.8001",
      icbm: "10.3833, 78.8001",
    },
    og: {
      type: "article",
      url: "https://barbequeen.com/blog/best-restaurant-in-pudukkottai",
      title: "Best Restaurant in Pudukkottai: Why Families Choose Barbequeen",
      description:
        "Discover wood-fire dum biryani, double-marinated grill chicken, and family-friendly dining at Barbequeen Pudukkottai. Served since 1999.",
      image: "https://barbequeen.com/images/barbequeen-pudukkottai-og.jpg",
      imageWidth: 1200,
      imageHeight: 630,
      siteName: "Barbequeen Restaurant",
      locale: "en_IN",
    },
    schema: pudukkottaiPageSchema,
  },
  "best-biryani-in-pudukkottai": {
    title: "Best Biryani in Pudukkottai: Wood-Fire Dum Biryani | Barbequeen",
    description:
      "Discover the best biryani in Pudukkottai at Barbequeen. Made with traditional Seeraga Samba rice and cooked on wood-fire dum for authentic flavor.",
    keywords:
      "best biryani in pudukkottai, wood fire dum biryani, seeraga samba biryani, best chicken biryani in pudukkottai, barbequeen pudukkottai, bucket biryani pudukkottai",
    robots: "index, follow",
    canonical: "https://barbequeen.com/blog/best-biryani-in-pudukkottai",
    geo: {
      region: "IN-TN",
      placename: "Pudukkottai, Tamil Nadu",
      position: "10.3833;78.8001",
      icbm: "10.3833, 78.8001",
    },
    og: {
      type: "article",
      url: "https://barbequeen.com/blog/best-biryani-in-pudukkottai",
      title: "Best Biryani in Pudukkottai: Wood-Fire Dum Biryani | Barbequeen",
      description:
        "Discover the best biryani in Pudukkottai at Barbequeen. Made with traditional Seeraga Samba rice and cooked on wood-fire dum for authentic flavor.",
      image: "https://barbequeen.com/images/best-biryani-in-pudukkottai.jpg",
      siteName: "Barbequeen",
      locale: "en_IN",
    },
  },
  "best-bbq-restaurant-pudukkottai": {
    title:
      "Charcoal Smoke & Biryani Steam: Best BBQ & Biryani in Pudukkottai | Barbequeen",
    description:
      "Looking for the best BBQ restaurant and chicken or mutton biryani in Pudukkottai? Visit Barbequeen for double-marinated grill chicken & wood-fired dum biryani.",
    keywords:
      "best BBQ restaurant in Pudukkottai, best chicken biryani in Pudukkottai, best mutton biryani in Pudukkottai, Barbequeen Pudukkottai, double marinated chicken, wood fired biryani, family restaurant Pudukkottai",
    robots: "index, follow",
    canonical: "https://barbequeen.com/blog/best-bbq-restaurant-pudukkottai",
    geo: {
      region: "IN-TN",
      placename: "Pudukkottai, Tamil Nadu",
      position: "10.3797;78.8208",
      icbm: "10.3797, 78.8208",
    },
    og: {
      type: "article",
      url: "https://barbequeen.com/blog/best-bbq-restaurant-pudukkottai",
      title:
        "Charcoal Smoke and Biryani Steam: The Best BBQ Restaurant in Pudukkottai",
      description:
        "Discover double-marinated grill chicken and wood-fired Arcot-style biryani at Barbequeen Pudukkottai. Taste the difference on your first visit!",
      image:
        "https://barbequeen.com/assets/images/blog/best-bbq-biryani-pudukkottai.jpg",
      siteName: "Barbequeen Restaurant",
      locale: "en_IN",
    },
    schema: bestBbqRestaurantInPudukkottaiSchema,
  },
  "best-bbq-restaurant-in-pudukkottai": {
    title:
      "Charcoal Smoke & Biryani Steam: Best BBQ & Biryani in Pudukkottai | Barbequeen",
    description:
      "Looking for the best BBQ restaurant and chicken or mutton biryani in Pudukkottai? Visit Barbequeen for double-marinated grill chicken & wood-fired dum biryani.",
    keywords:
      "best BBQ restaurant in Pudukkottai, best chicken biryani in Pudukkottai, best mutton biryani in Pudukkottai, Barbequeen Pudukkottai, double marinated chicken, wood fired biryani, family restaurant Pudukkottai",
    robots: "index, follow",
    canonical: "https://barbequeen.com/blog/best-bbq-restaurant-pudukkottai",
    geo: {
      region: "IN-TN",
      placename: "Pudukkottai, Tamil Nadu",
      position: "10.3797;78.8208",
      icbm: "10.3797, 78.8208",
    },
    og: {
      type: "article",
      url: "https://barbequeen.com/blog/best-bbq-restaurant-pudukkottai",
      title:
        "Charcoal Smoke and Biryani Steam: The Best BBQ Restaurant in Pudukkottai",
      description:
        "Discover double-marinated grill chicken and wood-fired Arcot-style biryani at Barbequeen Pudukkottai. Taste the difference on your first visit!",
      image:
        "https://barbequeen.com/assets/images/blog/best-bbq-biryani-pudukkottai.jpg",
      siteName: "Barbequeen Restaurant",
      locale: "en_IN",
    },
    schema: bestBbqRestaurantInPudukkottaiSchema,
  },
};

/* ─── Main ──────────────────────────────────────────────────────── */
const BlogDetailPage = () => {
  const { slug } = useParams();
  const location = useLocation();
  const currentSlug = slug || location.pathname.replace(/^\//, "");
  const isBlogRoute = location.pathname.startsWith("/blog");

  const post = useMemo(
    () =>
      blogPosts.find(
        (p) =>
          p.slug === currentSlug ||
          (currentSlug === "best-restaurant-in-coimabtore" &&
            p.slug === "best-restaurant-in-coimbatore"),
      ),
    [currentSlug],
  );
  const landingSEO = !isBlogRoute ? LANDING_PAGE_SEO[currentSlug] : null;

  const bodyBlocks = useMemo(
    () => (post ? cleanBlocks(post.contentBlocks) : []),
    [post],
  );
  const popularPosts = useMemo(
    () => blogPosts.filter((p) => p.slug !== currentSlug).slice(0, 3),
    [currentSlug],
  );

  if (!post && !landingSEO) {
    return (
      <div className="min-h-screen bg-[#f9f5f0]">
        <section
          className="relative overflow-hidden"
          style={{ minHeight: "320px" }}
        >
          <img
            src="https://images.unsplash.com/photo-1504674900247-0877df9cc836?q=80&w=1600&auto=format&fit=crop"
            alt="Article Not Found"
            className="absolute inset-0 w-full h-full object-cover object-center"
          />
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(to bottom, rgba(10,0,2,0.80) 0%, rgba(10,0,2,0.90) 100%)",
            }}
          />
          <div className="relative z-10 max-w-6xl mx-auto px-4 lg:px-8 py-28 text-center">
            <h1 className="text-3xl sm:text-5xl font-bold text-white uppercase mb-4">
              Article Not Found
            </h1>
            <p className="text-gray-300 text-base mb-6">
              The article you are looking for does not exist or has been moved.
            </p>
            <Link
              to="/blog"
              className="inline-block px-6 py-3 bg-[#9b1c2e] text-white text-sm font-semibold rounded-full hover:bg-[#7d1624] transition-colors"
            >
              ← Back to Blog
            </Link>
          </div>
        </section>
      </div>
    );
  }

  const seoProps = landingSEO || {
    title:
      post.seoTitle ||
      post.metaTitle ||
      (post.title ? `${post.title} | Barbequeen Blog` : "Barbequeen Blog"),
    metaTitle: post.metaTitle || post.seoTitle || post.title,
    description: post.metaDescription || post.excerpt || "",
    keywords: post.keywords || "",
    robots: post.robots || "index, follow",
    canonical: post.canonical || `https://barbequeen.com/blog/${post.slug}`,
    og: {
      type: post.og?.type || "article",
      url: post.og?.url || `https://barbequeen.com/blog/${post.slug}`,
      title: post.og?.title || post.title,
      description:
        post.og?.description || post.metaDescription || post.excerpt || "",
      image: post.og?.image || getFeaturedImage(post),
      siteName: post.og?.siteName || "Barbequeen Restaurant",
      locale: post.og?.locale || "en_IN",
    },
    schema: post.schema,
  };

  const cat = post ? getCategory(post) : "Food";
  const img = post
    ? getFeaturedImage(post)
    : landingSEO.og.image ||
      "https://images.unsplash.com/photo-1504674900247-0877df9cc836?q=80&w=1200&auto=format&fit=crop";
  const rt = post ? getReadTime(post) : 5;
  const pageTitle = post ? post.title : landingSEO.title;
  const pageUrl = typeof window !== "undefined" ? window.location.href : "";

  return (
    <div
      className="min-h-screen bg-[#f9f5f0]"
      style={{ fontFamily: "'Inter', 'Segoe UI', sans-serif" }}
    >
      <SEO {...seoProps} />
      {/* ── Hero Banner ── */}
      <section
        className="relative overflow-hidden"
        style={{ minHeight: "360px" }}
      >
        {/* Background image */}
        <img
          src={img}
          alt={post?.title || pageTitle || "Barbequeen"}
          className="absolute inset-0 w-full h-full object-cover object-center scale-105"
          onError={(e) => {
            e.target.src =
              "https://images.unsplash.com/photo-1504674900247-0877df9cc836?q=80&w=1200&auto=format&fit=crop";
          }}
        />

        {/* Dark overlay gradient */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(to bottom, rgba(10,0,2,0.82) 0%, rgba(10,0,2,0.92) 100%)",
          }}
        />

        {/* Hero Content */}
        <div className="relative z-10 max-w-6xl mx-auto px-4 lg:px-8 pt-28 sm:pt-32 pb-14 text-left">
          {/* Breadcrumbs */}
          <nav className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-white/60 mb-5">
            <Link to="/" className="hover:text-white transition-colors">
              Home
            </Link>
            <svg
              className="w-3 h-3 text-white/40"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9 5l7 7-7 7"
              />
            </svg>
            <Link to="/blog" className="hover:text-white transition-colors">
              Blog
            </Link>
            <svg
              className="w-3 h-3 text-white/40"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9 5l7 7-7 7"
              />
            </svg>
            <span className="text-white/90 font-medium truncate max-w-[200px] sm:max-w-xs">
              {post.title}
            </span>
          </nav>

          {/* Category Tag */}
          <span className="inline-block px-3 py-1 rounded-full text-white text-[10px] font-bold tracking-[0.2em] uppercase mb-4 bg-[#9b1c2e]">
            {cat}
          </span>

          {/* Title */}
          <h1 className="text-2xl sm:text-4xl lg:text-[40px] font-bold text-white leading-snug tracking-wide uppercase max-w-4xl mb-4">
            {post.title}
          </h1>

          {/* Meta Bar */}
          <div className="flex flex-wrap items-center gap-5 text-xs text-white/80 font-sans pt-2">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-full bg-[#9b1c2e] flex items-center justify-center text-white font-bold text-xs">
                B
              </div>
              <span className="font-semibold text-white">
                Barbequeen Editorial
              </span>
            </div>
            <span className="text-white/30">•</span>
            <div className="flex items-center gap-1.5 text-white/70">
              <svg
                className="w-3.5 h-3.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <span>{rt} min read</span>
            </div>
          </div>
        </div>
      </section>

      {/* ── Author / Meta & Social Share Row ── */}
      <div className="max-w-6xl mx-auto px-4 lg:px-8">
        <div className="flex flex-wrap items-center justify-between gap-4 py-5 border-b border-gray-200">
          {/* Author */}
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#9b1c2e] flex items-center justify-center flex-shrink-0 text-white font-bold text-sm">
              B
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-800">
                Barbequeen Editorial
              </p>
              <p className="text-xs text-gray-400">
                Barbequeen Restaurant, Salem
              </p>
            </div>
          </div>

          {/* Category + Read time */}
          <div className="flex items-center gap-3">
            <span className="px-3 py-1 rounded-full border border-gray-200 text-xs font-semibold text-gray-600 uppercase tracking-wide bg-white">
              {cat}
            </span>
            <span className="text-sm text-gray-500">{rt} min read</span>
          </div>

          {/* Share */}
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-400 font-medium">
              Share this post
            </span>
            <a
              href={`https://instagram.com/`}
              target="_blank"
              rel="noopener noreferrer"
              className="w-8 h-8 rounded-full border border-gray-200 flex items-center justify-center text-gray-500 hover:border-[#9b1c2e] hover:text-[#9b1c2e] transition-colors"
            >
              <svg
                className="w-3.5 h-3.5"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
              </svg>
            </a>
            <a
              href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(pageUrl)}&text=${encodeURIComponent(pageTitle)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="w-8 h-8 rounded-full border border-gray-200 flex items-center justify-center text-gray-500 hover:border-[#9b1c2e] hover:text-[#9b1c2e] transition-colors"
            >
              <svg
                className="w-3.5 h-3.5"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.746l7.73-8.835L1.254 2.25H8.08l4.253 5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
              </svg>
            </a>
            <a
              href={`https://facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="w-8 h-8 rounded-full border border-gray-200 flex items-center justify-center text-gray-500 hover:border-[#9b1c2e] hover:text-[#9b1c2e] transition-colors"
            >
              <svg
                className="w-3.5 h-3.5"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
              </svg>
            </a>
          </div>
        </div>
      </div>

      {/* ── Main Content + Sidebar ── */}
      <div className="max-w-6xl mx-auto px-4 lg:px-8 py-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* ── Article Body ── */}
          <main className="lg:col-span-8">
            {/* Excerpt */}
            {post.excerpt && (
              <p className="text-[16px] text-gray-600 leading-[1.8] mb-6 italic border-l-4 border-[#9b1c2e] pl-4">
                {renderFormattedText(post.excerpt)}
              </p>
            )}

            {/* Content Blocks */}
            <div className="prose-content">
              {bodyBlocks.map((block, idx) => {
                if (block.type === "heading") {
                  /* Long "headings" in the data are actually body text — render as paragraph */
                  if ((block.text || "").length > 120) {
                    return (
                      <p
                        key={idx}
                        className="text-[15px] text-gray-600 leading-[1.85] mb-4"
                      >
                        {renderFormattedText(block.text)}
                      </p>
                    );
                  }
                  return (
                    <h2
                      key={idx}
                      className="text-[18px] font-bold text-gray-900 leading-snug mt-8 mb-3"
                    >
                      {renderFormattedText(block.text)}
                    </h2>
                  );
                }
                if (block.type === "list_item") {
                  return (
                    <div key={idx} className="flex gap-3 items-start mb-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#9b1c2e] mt-2.5 flex-shrink-0" />
                      <p className="text-[15px] text-gray-600 leading-[1.8] flex-1">
                        {renderFormattedText(block.text)}
                      </p>
                    </div>
                  );
                }
                return (
                  <p
                    key={idx}
                    className="text-[15px] text-gray-600 leading-[1.85] mb-4"
                  >
                    {renderFormattedText(block.text)}
                  </p>
                );
              })}
            </div>

            {/* Interactive FAQ Section */}
            {post.faqs && post.faqs.length > 0 && (
              <div className="mt-12 pt-8 border-t border-gray-200">
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900 uppercase tracking-wide mb-6 flex items-center gap-2">
                  <span className="w-2.5 h-6 bg-[#9b1c2e] rounded-sm inline-block"></span>
                  Frequently Asked Questions
                </h3>
                <div className="space-y-4">
                  {post.faqs.map((faq, i) => (
                    <details
                      key={i}
                      className="group bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden open:shadow-md transition-all duration-200"
                    >
                      <summary className="cursor-pointer p-4 sm:p-5 font-semibold text-gray-800 text-[15px] flex items-center justify-between gap-4 list-none select-none hover:text-[#9b1c2e] transition-colors">
                        <span>{faq.question}</span>
                        <span className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0 group-open:rotate-180 group-open:bg-[#9b1c2e] group-open:text-white transition-transform">
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 9l-7 7-7-7"
                            />
                          </svg>
                        </span>
                      </summary>
                      <div className="px-4 sm:px-5 pb-5 pt-1 text-[14px] text-gray-600 leading-relaxed border-t border-gray-100">
                        {renderFormattedText(faq.answer)}
                      </div>
                    </details>
                  ))}
                </div>
              </div>
            )}

            {/* Tags / Share bottom */}
            <div className="mt-12 pt-8 border-t border-gray-100 flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-400 font-medium">
                  Share:
                </span>
                <a
                  href={`https://wa.me/?text=${encodeURIComponent(pageTitle + " " + pageUrl)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-8 h-8 rounded-full bg-[#25d366] flex items-center justify-center text-white hover:opacity-90 transition-opacity"
                >
                  <svg
                    className="w-3.5 h-3.5"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                  </svg>
                </a>
                <a
                  href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(pageUrl)}&text=${encodeURIComponent(pageTitle)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-8 h-8 rounded-full bg-black flex items-center justify-center text-white hover:opacity-90 transition-opacity"
                >
                  <svg
                    className="w-3.5 h-3.5"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.746l7.73-8.835L1.254 2.25H8.08l4.253 5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                  </svg>
                </a>
                <a
                  href={`https://facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-8 h-8 rounded-full bg-[#1877f2] flex items-center justify-center text-white hover:opacity-90 transition-opacity"
                >
                  <svg
                    className="w-3.5 h-3.5"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                  </svg>
                </a>
              </div>
              <Link
                to="/blog"
                className="text-sm text-gray-400 hover:text-[#9b1c2e] transition-colors flex items-center gap-1.5"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
                Back to Blog
              </Link>
            </div>
          </main>

          {/* ── Sidebar ── */}
          <aside className="lg:col-span-4">
            <div className="lg:sticky lg:top-6 space-y-8">
              {/* Popular Posts */}
              <div>
                <h3
                  className="text-xl font-bold text-gray-900 mb-6"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  Popular Post
                </h3>
                <div className="divide-y divide-gray-100">
                  {popularPosts.map((p, i) => (
                    <div key={p.id || i} className={i > 0 ? "pt-6" : ""}>
                      <PopularCard post={p} />
                    </div>
                  ))}
                </div>
              </div>

              {/* Divider */}
              <div className="border-t border-gray-100" />

              {/* Book Table CTA */}
              <div className="bg-[#0e0003] rounded-2xl p-7 text-center relative overflow-hidden">
                <div
                  className="absolute inset-0 opacity-20"
                  style={{
                    background:
                      "radial-gradient(circle at center, #9b1c2e, transparent)",
                  }}
                />
                <div className="relative z-10">
                  <p className="text-xs font-bold tracking-[0.3em] uppercase text-[#c0424e] mb-2">
                    Reserve a Table
                  </p>
                  <h4
                    className="text-xl font-bold text-white mb-3"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    Dine With Us
                  </h4>
                  <p className="text-sm text-gray-400 leading-relaxed mb-5">
                    Book a table at Barbequeen and enjoy an unforgettable dining
                    experience in Salem.
                  </p>
                  <Link
                    to="/reservation"
                    className="block w-full py-3 bg-[#9b1c2e] hover:bg-[#7d1624] text-white text-sm font-semibold tracking-wide rounded-lg text-center transition-colors"
                  >
                    Book Now
                  </Link>
                  <a
                    href="tel:+919597228788"
                    className="block mt-2 text-xs text-gray-500 hover:text-white transition-colors"
                  >
                    +91 95972 28788
                  </a>
                </div>
              </div>

              {/* Order CTA */}
              <div className="bg-gradient-to-br from-[#9b1c2e] to-[#7d1624] rounded-2xl p-7 text-center">
                <p className="text-xs font-bold tracking-[0.3em] uppercase text-white/60 mb-2">
                  Hungry?
                </p>
                <h4
                  className="text-xl font-bold text-white mb-3"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  Order Online
                </h4>
                <p className="text-sm text-white/70 leading-relaxed mb-5">
                  Get your favourite Barbequeen dishes delivered hot and fresh.
                </p>
                <Link
                  to="/menu"
                  className="block w-full py-3 bg-white text-[#9b1c2e] text-sm font-semibold rounded-lg hover:bg-gray-100 transition-colors"
                >
                  Order Now →
                </Link>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default BlogDetailPage;
